import { useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import useSWR from 'swr'
import { useAuth } from '../auth/AuthContext'
import { apiGet } from '../config/api'
import { useNavLayout } from '../layout/NavLayoutContext'
import type { NavLayoutMode } from '@proffer/shared'
import type { NotificationsListResponse } from '../types/notification'
import { AppTopNavMenu } from './AppTopNavMenu'
import {
  appNavGroups,
  appNavHomeItem,
  filterNavGroups,
  filterNavItems,
  getActiveNavGroupTitle,
  getActiveNavItem,
  navGroupsCollapsedByDefault,
  IconCog,
  IconLogout,
  IconWallet,
  navHeaderIconTone,
  type AppNavItem,
} from './appNavConfig'
import { PageInfoProvider } from '../layout/PageInfoContext'
import { AppFooter } from './AppFooter'
import { ProfferLogoMark } from './ProfferLogoMark'
import { SidebarToggleButton } from './SidebarToggleButton'
import { ThemeSwitcher } from './ThemeSwitcher'
import { ProTrialBanner } from './ProTrialBanner'
import { AccountPlanBadge } from './AccountPlanBadge'
import { NotificationBellButton } from './NotificationBellButton'
import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
  useSidebarCollapsed,
} from '../hooks/useSidebarCollapsed'

const toneActiveSidebar: Record<AppNavItem['tone'], string> = {
  violet: 'bg-violet-600 text-white shadow-md shadow-violet-600/25',
  emerald: 'bg-emerald-600 text-white shadow-md shadow-emerald-600/25',
  amber: 'bg-amber-500 text-white shadow-md shadow-amber-500/25',
  sky: 'bg-sky-600 text-white shadow-md shadow-sky-600/25',
  zinc: 'bg-zinc-800 text-white shadow-md shadow-zinc-600/25 dark:bg-zinc-200 dark:text-zinc-900',
  bronze: 'bg-orange-600 text-white shadow-md shadow-orange-600/25',
  silver: 'bg-slate-500 text-white shadow-md shadow-slate-500/25',
  gold: 'bg-yellow-600 text-white shadow-md shadow-yellow-600/25',
}

function useAppNavState() {
  const { user } = useAuth()
  const { pathname } = useLocation()
  const entitlements = user?.entitlements ?? []

  const { data: notifData } = useSWR(
    entitlements.includes('notifications') ? 'notifications' : null,
    () => apiGet<NotificationsListResponse>('/notifications'),
  )

  const topItems = filterNavItems([appNavHomeItem], entitlements)
  const groups = filterNavGroups(appNavGroups, entitlements)
  const activeGroupTitle = getActiveNavGroupTitle(pathname, topItems, groups)
  const activeItem = getActiveNavItem(pathname, topItems, groups)
  const unreadCount = notifData?.unreadCount ?? 0
  const navItems = [...topItems, ...groups.flatMap((g) => g.items)]

  return {
    user,
    pathname,
    topItems,
    groups,
    navItems,
    activeGroupTitle,
    activeItem,
    unreadCount,
  }
}

function isItemActive(item: AppNavItem, pathname: string): boolean {
  if (item.isActive) return item.isActive(pathname)
  if (item.end) return pathname === item.to
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}

function AppLogo({ compact = false }: { compact?: boolean }) {
  return (
    <Link to="/" className="group flex min-w-0 items-center gap-3">
      <ProfferLogoMark className="h-10 w-10 transition group-hover:scale-[1.02] sm:h-11 sm:w-11" />
      {!compact && (
        <span className="min-w-0">
          <span className="block truncate text-sm font-bold tracking-tight text-zinc-900 dark:text-white">
            ProfferAI
          </span>
          <span className="block truncate text-[10px] font-medium uppercase tracking-[0.18em] text-zinc-400 dark:text-zinc-500">
            Προσφορές
          </span>
        </span>
      )}
    </Link>
  )
}

function UserInitial({ email }: { email?: string }) {
  const letter = (email?.trim()[0] ?? 'P').toUpperCase()
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 text-sm font-bold text-white shadow-sm">
      {letter}
    </span>
  )
}

function accountTopLinkClass(isActive: boolean) {
  return `inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
    isActive
      ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-white'
      : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'
  }`
}

function TopBarActions() {
  const { showTopMenu, showSidebar } = useNavLayout()
  const topMenuOnly = showTopMenu && !showSidebar

  return (
    <div className="flex shrink-0 items-center gap-2 sm:gap-3">
      <AccountPlanBadge compact className="hidden min-w-0 sm:inline-flex" />
      <div
        className={`items-center gap-1 rounded-2xl border border-zinc-200/80 bg-zinc-50/80 p-1 dark:border-zinc-700/80 dark:bg-zinc-900/60 ${
          topMenuOnly ? 'flex' : 'hidden md:flex'
        }`}
      >
        <NavLink
          to="/billing"
          className={({ isActive }) => accountTopLinkClass(isActive)}
        >
          <IconWallet className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
          Συνδρομή
        </NavLink>
        <NavLink
          to="/settings"
          className={({ isActive }) => accountTopLinkClass(isActive)}
          title="Ρυθμίσεις"
        >
          <IconCog className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
          Ρυθμίσεις
        </NavLink>
      </div>
      <NotificationBellButton />
      <ThemeSwitcher />
    </div>
  )
}

function HeaderPageContext({
  item,
  groupTitle,
}: {
  item: AppNavItem
  groupTitle: string
}) {
  const { Icon, label, tone } = item

  return (
    <div
      className="flex min-w-0 max-w-[min(100%,18rem)] items-center gap-2 sm:max-w-xs"
      aria-label={groupTitle ? `${groupTitle}: ${label}` : label}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${navHeaderIconTone[tone]}`}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 leading-tight">
        {groupTitle ? (
          <p className="truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-violet-600 dark:text-violet-400">
            {groupTitle}
          </p>
        ) : null}
        <p className="truncate text-sm font-semibold text-zinc-900 dark:text-white">
          {label}
        </p>
      </div>
    </div>
  )
}

function AppTopBar({
  layoutMode,
  showLogo,
  showTopNav,
  showMobileNav,
}: {
  layoutMode: NavLayoutMode
  showLogo: boolean
  showTopNav: boolean
  showMobileNav: boolean
}) {
  const { activeGroupTitle, activeItem } = useAppNavState()
  /** Τίτλος στο header μόνο χωρίς top menu (πλαϊνή στήλη). Με «και τα 2» → sidebar + tabs. */
  const showHeaderContext = Boolean(activeItem) && layoutMode === 'sidebar'
  const showMobilePageStrip = Boolean(activeItem) && layoutMode === 'both'
  const useToolbarLayout = showTopNav
  const toolbarBalanced =
    useToolbarLayout && !showHeaderContext && !showLogo

  return (
    <header className="relative z-50 shrink-0 border-b border-zinc-200/70 bg-white/95 shadow-[0_1px_0_rgba(0,0,0,0.03)] backdrop-blur-2xl dark:border-zinc-800/70 dark:bg-zinc-950/95 dark:shadow-[0_1px_0_rgba(255,255,255,0.04)]">
      <div className="mx-auto w-full max-w-app-wide px-4 sm:px-6">
        {useToolbarLayout ? (
          <div
            className={`grid h-14 items-center gap-2 sm:gap-4 ${
              toolbarBalanced
                ? 'grid-cols-1 justify-items-end md:grid-cols-[1fr_auto_1fr] md:justify-items-stretch'
                : 'grid-cols-[auto_1fr_auto]'
            }`}
          >
            <div className="flex min-w-0 items-center justify-start gap-2 sm:gap-3">
              {showLogo ? <AppLogo compact /> : null}
              {showHeaderContext && activeItem ? (
                <HeaderPageContext
                  item={activeItem}
                  groupTitle={activeGroupTitle}
                />
              ) : null}
            </div>

            <nav
              className="hidden min-w-0 justify-center overflow-x-auto md:flex [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              aria-label="Κύρια πλοήγηση"
            >
              <div className="inline-flex gap-0.5">
                <AppTopNavMenu />
              </div>
            </nav>

            <div className="flex justify-end">
              <TopBarActions />
            </div>
          </div>
        ) : (
          <div className="flex h-[3.75rem] items-center justify-between gap-3 sm:h-16">
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              {showLogo ? <AppLogo /> : null}
              {showHeaderContext && activeItem ? (
                <HeaderPageContext
                  item={activeItem}
                  groupTitle={activeGroupTitle}
                />
              ) : null}
            </div>
            <TopBarActions />
          </div>
        )}

        {showMobilePageStrip && activeItem ? (
          <div className="border-t border-zinc-100 px-1 py-2.5 lg:hidden dark:border-zinc-800/80">
            <HeaderPageContext item={activeItem} groupTitle={activeGroupTitle} />
          </div>
        ) : null}

        {showTopNav ? (
          <nav
            className="border-t border-zinc-100 py-2 dark:border-zinc-800/80 md:hidden"
            aria-label="Κύρια πλοήγηση"
          >
            <div className="flex gap-0.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <AppTopNavMenu />
            </div>
          </nav>
        ) : null}

        {showMobileNav ? (
          <nav
            className="border-t border-zinc-100 py-2 dark:border-zinc-800/80 lg:hidden"
            aria-label="Κύρια πλοήγηση"
          >
            <AppMobileNavLinks />
          </nav>
        ) : null}
      </div>
    </header>
  )
}

function NavBadge({
  count,
  compact = false,
}: {
  count: number
  compact?: boolean
}) {
  if (count <= 0) return null
  if (compact) {
    return (
      <span
        className="absolute right-1 top-1 h-2 w-2 rounded-full bg-white ring-2 ring-violet-600 dark:ring-violet-500"
        aria-label={`${count} μη αναγνωσμένα`}
      />
    )
  }
  return (
    <span className="ml-auto inline-flex min-w-[1.25rem] justify-center rounded-full bg-white/25 px-1.5 text-[10px] font-bold leading-5 text-white">
      {count > 99 ? '99+' : count}
    </span>
  )
}

function AppSidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean
  onToggle: () => void
}) {
  const { user, logout } = useAuth()
  const { pathname, topItems, groups, unreadCount } = useAppNavState()
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        Array.from(navGroupsCollapsedByDefault).map((id) => [id, true]),
      ),
  )

  const renderSidebarLink = (item: AppNavItem) => {
    const active = isItemActive(item, pathname)
    const badge = item.to === '/notifications' ? unreadCount : 0
    const { Icon } = item
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        title={collapsed ? item.label : undefined}
        aria-current={active ? 'page' : undefined}
        className={`group relative flex items-center rounded-2xl text-sm font-semibold transition ${
          collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
        } ${
          active
            ? toneActiveSidebar[item.tone]
            : 'text-zinc-600 hover:bg-zinc-100/90 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-800/80 dark:hover:text-white'
        }`}
      >
        <Icon
          className={`h-5 w-5 shrink-0 transition ${
            active
              ? 'text-white/95'
              : 'text-zinc-400 group-hover:text-zinc-600 dark:group-hover:text-zinc-300'
          }`}
        />
        {!collapsed ? <span className="truncate">{item.label}</span> : null}
        {badge > 0 ? <NavBadge count={badge} compact={collapsed} /> : null}
      </NavLink>
    )
  }

  return (
    <aside
      style={{
        width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
      }}
      className="fixed inset-y-0 left-0 z-30 hidden h-dvh flex-col overflow-hidden border-r border-zinc-200/70 bg-gradient-to-b from-white via-white to-zinc-50/90 transition-[width] duration-200 ease-out dark:border-zinc-800/70 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900/80 lg:flex"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-[radial-gradient(ellipse_80%_70%_at_50%_0%,rgba(124,58,237,0.12),transparent)] dark:bg-[radial-gradient(ellipse_80%_70%_at_50%_0%,rgba(124,58,237,0.08),transparent)]" />

      <div
        className={`relative flex shrink-0 gap-2 pb-4 pt-5 ${
          collapsed
            ? 'flex-col items-center px-2'
            : 'items-start justify-between px-4'
        }`}
      >
        <AppLogo compact={collapsed} />
        <SidebarToggleButton
          expanded={!collapsed}
          onToggle={onToggle}
          className="shrink-0"
        />
      </div>

      <div
        className={`relative min-h-0 flex-1 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-300 dark:[&::-webkit-scrollbar-thumb]:bg-zinc-700 ${
          collapsed ? 'px-2' : 'px-4 pr-3'
        }`}
      >
        <div className={collapsed ? 'space-y-2 pb-4' : 'space-y-6 pb-4'}>
          {topItems.length > 0 ? (
            <nav className="space-y-0.5" aria-label="Αρχική">
              {topItems.map((item) => renderSidebarLink(item))}
            </nav>
          ) : null}
          {groups.map((group) => {
            const groupCollapsed = collapsedGroups[group.id] ?? false
            const groupActive = group.items.some((item) =>
              isItemActive(item, pathname),
            )
            const showItems = !groupCollapsed || groupActive
            return (
              <div key={group.id}>
                {!collapsed ? (
                  navGroupsCollapsedByDefault.has(group.id) ? (
                    <button
                      type="button"
                      className="mb-2 flex w-full items-center justify-between px-2 text-left text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400 transition hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300"
                      aria-expanded={showItems}
                      onClick={() =>
                        setCollapsedGroups((prev) => ({
                          ...prev,
                          [group.id]: !groupCollapsed,
                        }))
                      }
                    >
                      <span>{group.title}</span>
                      <span aria-hidden>{showItems ? '▾' : '▸'}</span>
                    </button>
                  ) : (
                    <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400 dark:text-zinc-500">
                      {group.title}
                    </p>
                  )
                ) : null}
                {showItems ? (
                  <nav className="space-y-0.5" aria-label={group.title}>
                    {group.items.map((item) => renderSidebarLink(item))}
                  </nav>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>

      <div
        className={`relative mt-auto shrink-0 border-t border-zinc-200/80 bg-white/95 py-4 backdrop-blur-sm dark:border-zinc-800/80 dark:bg-zinc-950/95 ${
          collapsed ? 'px-2' : 'px-4'
        }`}
      >
        <div
          className={`rounded-2xl border border-zinc-200/80 bg-zinc-50/90 shadow-app-soft dark:border-zinc-800 dark:bg-zinc-900/70 ${
            collapsed ? 'flex flex-col items-center gap-2 p-2' : 'p-3'
          }`}
        >
          {collapsed ? (
            <>
              <AccountPlanBadge compact className="mb-1 justify-center" />
              <Link
                to="/settings"
                title="Ρυθμίσεις"
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-zinc-200 bg-white text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800"
              >
                <IconCog className="h-4 w-4" aria-hidden />
              </Link>
              <button
                type="button"
                title="Έξοδος"
                onClick={() => logout()}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-900 text-white transition hover:bg-zinc-700 dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-200"
              >
                <IconLogout className="h-4 w-4" aria-hidden />
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <UserInitial email={user?.email} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-zinc-900 dark:text-white">
                    {user?.businessName?.trim() || 'Ο λογαριασμός μου'}
                  </p>
                  <p
                    className="truncate text-[11px] text-zinc-500 dark:text-zinc-400"
                    title={user?.email}
                  >
                    {user?.email}
                  </p>
                  <div className="mt-1.5">
                    <AccountPlanBadge compact />
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Link
                  to="/settings"
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-zinc-200 bg-zinc-50 px-2 py-2 text-xs font-semibold text-zinc-700 transition hover:bg-white dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                  <IconCog className="h-4 w-4 shrink-0 opacity-80" />
                  Ρυθμίσεις
                </Link>
                <button
                  type="button"
                  onClick={() => logout()}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-zinc-900 px-2 py-2 text-xs font-semibold text-white transition hover:bg-zinc-700 dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-200"
                >
                  <IconLogout className="h-4 w-4 shrink-0 opacity-90" />
                  Έξοδος
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </aside>
  )
}

function AppMobileNavLinks() {
  const { pathname, navItems, unreadCount } = useAppNavState()
  const items = navItems

  return (
    <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((item) => {
        const active = isItemActive(item, pathname)
        const badge = item.to === '/notifications' ? unreadCount : 0
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition ${
              active
                ? 'bg-violet-600 text-white shadow-md shadow-violet-600/25'
                : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'
            }`}
          >
            {item.label}
            {badge > 0 ? (
              <span
                className={`inline-flex min-w-[1.15rem] justify-center rounded-full px-1 text-[10px] font-bold leading-5 ${
                  active ? 'bg-white/25 text-white' : 'bg-violet-600 text-white'
                }`}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            ) : null}
          </NavLink>
        )
      })}
    </div>
  )
}

function AppBackground() {
  return (
    <>
      <div
        className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(ellipse_100%_70%_at_50%_-10%,rgba(99,102,241,0.12),transparent)] dark:bg-[radial-gradient(ellipse_100%_70%_at_50%_-10%,rgba(99,102,241,0.06),transparent)]"
        aria-hidden
      />
      <div
        className="pointer-events-none fixed bottom-0 left-1/2 -z-10 h-[30vh] w-[min(100%,72rem)] -translate-x-1/2 bg-[radial-gradient(circle_at_50%_100%,rgba(139,92,246,0.05),transparent_70%)]"
        aria-hidden
      />
    </>
  )
}

export function AppShell() {
  const { mode: layoutMode, showTopMenu, showSidebar } = useNavLayout()
  const { collapsed, toggle } = useSidebarCollapsed('user')
  const sidebarOnly = showSidebar && !showTopMenu
  const showMobileNav = sidebarOnly
  const showLogoInHeader = !showSidebar

  return (
    <div className="relative h-dvh overflow-hidden bg-zinc-50 text-zinc-900 transition-colors dark:bg-zinc-950 dark:text-zinc-50">
      <AppBackground />

      {showSidebar ? (
        <AppSidebar collapsed={collapsed} onToggle={toggle} />
      ) : null}

      <div
        className={`flex h-dvh min-w-0 flex-col overflow-hidden transition-[padding] duration-200 ${
          showSidebar
            ? collapsed
              ? 'lg:pl-[4.5rem]'
              : 'lg:pl-[17.5rem]'
            : ''
        }`}
      >
        <AppTopBar
          layoutMode={layoutMode}
          showLogo={showLogoInHeader}
          showTopNav={showTopMenu}
          showMobileNav={showMobileNav}
        />

        <ProTrialBanner />

        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <PageInfoProvider>
            <Outlet />
          </PageInfoProvider>
        </main>

        <div className="shrink-0">
          <AppFooter />
        </div>
      </div>
    </div>
  )
}
